import { withZod } from '@remix-validated-form/with-zod'
import { useState } from 'react'
import { z } from 'zod'
import Button from '../Button'
import { useConfirmation } from '../ConfirmationContext'
import { FormColorSelector } from '../form/FormColorSelector'
import FormInput from '../form/FormInput'
import { SmartForm } from '../form/SmartForm'
import { Checkbox } from 'app/components/ui/checkbox'
import type { StageType } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { getPipelineColors } from 'lib/getPipelineColors'
import { debug } from 'lib/logging'

const pipelineSchema = withZod(
  z.object({
    name: z
      .string()
      .min(1, { message: `Stage name is required` })
      .max(255, { message: `Stage name must be less than 255 characters` })
      .transform((value) => value.replace(/\//g, ` `)),
    color: z.string().min(1, { message: `Stage color is required` }),
    index: z
      .string()
      .optional()
      .transform((value) => (value ? Number(value) : -1)),
  }),
)
const stageDeleteSchema = withZod(
  z.object({
    index: z
      .string()
      .min(1, { message: `Index is required` })
      .transform((value) => (value ? Number(value) : -1)),
  }),
)

export default function EditPipeline({
  stage,
  index,
  type = `ADD`,
  updatePipelineStage,
  deleteStage,
  structureLocked = false,
  defaultStage,
}: {
  stage?: StageType
  index: number
  type: `ADD` | `UPDATE`
  updatePipelineStage: (
    stage: StageType,
    index: number,
    options?: { makeDefault?: boolean },
  ) => Promise<void | { error?: string }>
  deleteStage: (index: number) => void
  /**
   * A list's stage, where one stage is the default: where new entries, and members with no
   * stage yet, land. `isDefault` is this stage's standing; a list's first stage is always the
   * default, so the box is then checked and cannot be cleared (choosing another stage is how
   * the default moves). Absent on the Pipelines page, which has no default.
   */
  defaultStage?: { isDefault: boolean }
  // True for a stage on a system pipeline (Action Center-owned). Its name and existence are
  // structure the board depends on, so both stay read-only here; colour has no such dependency
  // and is left editable. The caller (usePipelinesPage) refuses the underlying mutation too,
  // this only keeps the control from offering what it would refuse.
  structureLocked?: boolean
}): React.ReactElement {
  const [confirm] = useConfirmation()
  const [loading, setLoading] = useState<boolean>(false)
  const [deleting, setDeleting] = useState<boolean>(false)
  const [nameError, setNameError] = useState<string | undefined>(undefined)
  const [makeDefault, setMakeDefault] = useState<boolean>(!!defaultStage?.isDefault)
  const handleUpdate = async () => {
    setLoading(true)
    let form = document.getElementById(`update-pipeline`) as HTMLFormElement
    let data = Object.fromEntries(new FormData(form).entries())
    let validateResult = await pipelineSchema.validate(data)
    if (validateResult.error) {
      debug(`pipelines`, `error`, validateResult.error)
      if (validateResult?.error?.fieldErrors?.name) {
        setNameError(validateResult?.error?.fieldErrors?.name)
      }
      setLoading(false)
      return
    } else if (nameError) {
      setNameError(undefined)
      setLoading(false)
    }
    let response = await updatePipelineStage(
      {
        order: index + 1,
        name: data.name as string,
        color: data.color as string,
      },
      index,
      { makeDefault: makeDefault && !defaultStage?.isDefault },
    )
    if (response && response.error != null) {
      debug(`pipeline`, `error on adding/editing stage`, response.error)
      notifyError(`Stage name already used in pipeline`, response.error)
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    await confirm({
      title: `Delete stage`,
      description: `Are you sure you want to delete this stage?`,
      variant: `danger`,
    }).then(async () => {
      setDeleting(true)
      let form = document.getElementById(`update-pipeline`) as HTMLFormElement
      let data = Object.fromEntries(new FormData(form).entries())
      let validateResult = await stageDeleteSchema.validate(data)
      if (validateResult.error) {
        setDeleting(false)
        debug(`pipelines`, `error`, validateResult.error)
        if (validateResult?.error?.fieldErrors?.name) {
          notifyError(validateResult?.error?.fieldErrors?.name)
        }
        return
      } else {
        deleteStage(Number(data.index))
      }
    })
  }

  return (
    <SmartForm
      id="update-pipeline"
      className="!mt-0 min-h-min w-[680px] !gap-y-0 px-6 !text-sm !font-medium !leading-5"
      validator={pipelineSchema}
      defaultValues={{ ...stage }}
      hiddenInputs={[{ name: `index`, value: `${index || 0}` }]}
      preventSubmit
      hideButtons
    >
      <FormInput
        name="name"
        label="Stage"
        placeholder={`Eg: Stage ${index === -1 ? 1 : index + 1}`}
        wrapperClassName="mt-5 mb-1.5 text-sm font-medium leading-5 text-strong"
        cols={6}
        error={nameError}
        errorClassName="!mt-0.5"
        disabled={structureLocked}
        infoTooltip={
          structureLocked
            ? {
                title: `Locked`,
                message: `This stage is part of the Action Center. Its name can’t be changed here, but its colour can.`,
              }
            : undefined
        }
      />
      <FormColorSelector
        name="color"
        defaultColor="pipelines-silver"
        presetColors={getPipelineColors()}
        colorClassName="h-8 w-8 grow cursor-pointer flex space-x-2"
        wrapperClassName="flex items-center mt-4"
        cols={6}
      />
      {defaultStage && (
        <label className="col-span-6 mt-4 flex cursor-pointer items-start gap-2">
          <Checkbox
            className="mt-0.5"
            checked={makeDefault}
            disabled={defaultStage.isDefault}
            onCheckedChange={(checked) => setMakeDefault(checked === true)}
          />
          <span className="flex flex-col gap-0.5">
            <span className="text-body-sm font-medium text-strong">Default stage</span>
            <span className="text-body-xs text-subtle">
              {defaultStage.isDefault
                ? `New entries, and anyone without a stage yet, start here. Make another stage the default to change it.`
                : `New entries, and anyone without a stage yet, will start here.`}
            </span>
          </span>
        </label>
      )}
      <div
        className={`col-span-6 mt-4 flex border-t border-default py-3 ${
          type === `ADD` || structureLocked ? `justify-end` : `justify-between`
        }`}
      >
        {type !== `ADD` && !structureLocked && (
          <Button label="Delete" mode="danger" disabled={loading} onClick={handleDelete} loading={deleting} />
        )}
        <div className="flex justify-end gap-x-2">
          <Button label="Cancel" mode="white" disabled={loading || deleting} onClick={closeModal} />
          <Button
            label="Save"
            mode="primary"
            form="new-list-pipeline"
            type="submit"
            onClick={handleUpdate}
            disabled={deleting}
            loading={loading}
          />
        </div>
      </div>
    </SmartForm>
  )
}
